import {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    LimitOrderIF,
    PositionIF,
    PoolIF,
    TokenIF,
    TransactionIF,
} from '../../ambient-utils/types';
import matchSearchInput from '../functions/matchSearchInput';
import { tokenMethodsIF } from './useTokens';
import { ZERO_ADDRESS, tokenListURIs } from '../../ambient-utils/constants';
import { PoolContext } from '../../contexts/PoolContext';

export interface sidebarSearchIF {
    rawInput: string;
    setInput: Dispatch<SetStateAction<string>>;
    clearInput: () => void;
    isInputValid: boolean;
    pools: PoolIF[];
    positions: PositionIF[];
    txs: TransactionIF[];
    limits: LimitOrderIF[];
}

export const useSidebarSearch = (
    positionList: PositionIF[],
    txList: TransactionIF[],
    limitOrderList: LimitOrderIF[],
    tokens: tokenMethodsIF,
): sidebarSearchIF => {
    const { poolList } = useContext(PoolContext);

    // raw user input from the DOM
    const [rawInput, setRawInput] = useState<string>('');

    const [dbInput, setDbInput] = useState<string>('');

    useEffect(() => {
        const timer = setTimeout(() => setDbInput(rawInput), 400);
        return () => clearTimeout(timer);
    }, [rawInput]);

    // search type ➜ '' or 'address' or 'nameOrAddress'
    const [searchAs, setSearchAs] = useState<string | null>(null);

    // fn to clear the search input
    function clearInput(): void {
        setRawInput('');
    }

    // cleaned and validated version of raw user input
    const validatedInput = useMemo<string>(() => {
        // trim string and make it lower case
        const cleanInput: string = dbInput.trim().toLowerCase();
        // action if input appears to be a contract address
        if (
            cleanInput.length === 42 ||
            (cleanInput.length === 40 && !cleanInput.startsWith('0x'))
        ) {
            setSearchAs('address');
            // if not an apparent token address, search name and symbol
        } else if (cleanInput.length > 0) {
            setSearchAs('nameOrSymbol');
            return cleanInput;
            // otherwise treat as if there is no input entered
        } else {
            setSearchAs(null);
            return '';
        }
        // add '0x' to the front of the cleaned string if not present
        const fixedInput = cleanInput.startsWith('0x')
            ? cleanInput
            : '0x' + cleanInput;
        // declare an output variable
        let output = cleanInput;
        // check if string is a correctly-formed contract address
        if (
            // check if string has 42 characters
            fixedInput.length === 42 &&
            // check if string after '0x' is valid hexadecimal
            fixedInput.substring(2).match(/[0-9a-f]/g)
        ) {
            // if fixed string is valid, assign it to the output variable
            output = fixedInput;
        }
        // return output variable
        return output;
    }, [dbInput]);

    // array of pools to output from the hook
    const [outputPools, setOutputPools] = useState<PoolIF[]>([]);

    // constant to force searches to return exact matches only
    const SEARCH_EXACT = true;

    // logic to update the output pools from the hook
    useEffect(() => {
        // fn to filter pools by address (must be exact)
        const searchByAddress = (addr: string): PoolIF[] =>
            poolList.filter((pool: PoolIF) =>
                matchSearchInput(
                    addr,
                    [pool.base.address, pool.quote.address],
                    SEARCH_EXACT,
                ),
            );
        // fn to filter pools by symbol (must be exact IF input is two characters)
        const searchByNameOrSymbol = (symb: string): PoolIF[] =>
            poolList
                .filter((pool: PoolIF) =>
                    matchSearchInput(symb, [
                        pool.base.symbol,
                        pool.quote.symbol,
                        pool.base.name,
                        pool.quote.name,
                    ]),
                )
                .filter(
                    (pool: PoolIF) =>
                        tokens.verify(pool.base.address) &&
                        tokens.verify(pool.quote.address),
                );
        // fn to return list of verified pools with no search filtering
        const noSearch = (): PoolIF[] =>
            poolList.filter(
                (pool: PoolIF) =>
                    tokens.verify(pool.base.address) &&
                    tokens.verify(pool.quote.address),
            );
        // variable to hold the list of pools generated by the search
        let filteredPools: PoolIF[];
        // logic router to apply the relevant search function
        switch (searchAs) {
            case 'address':
                filteredPools = searchByAddress(validatedInput);
                break;
            case 'nameOrSymbol':
                filteredPools = searchByNameOrSymbol(validatedInput);
                break;
            case '':
            default:
                filteredPools = noSearch();
        }
        // sort returned pools before sending data to the app
        const sortedPools: PoolIF[] = filteredPools.sort(
            (poolA: PoolIF, poolB: PoolIF) => {
                // logic to assign numerical priority to a pool (scale is arbitrary)
                function getPoolPriority(pool: PoolIF): number {
                    // fn to increment the output value
                    function getTokenPriority(tkn: TokenIF): number {
                        let count: number;
                        if (tkn.address === ZERO_ADDRESS) {
                            count = Object.keys(tokenListURIs).length;
                        } else if (tkn.listedBy) {
                            count = tkn.listedBy.length;
                        } else {
                            count = 1;
                        }
                        return count;
                    }
                    // return overall priority value
                    return (
                        getTokenPriority(pool.base) +
                        getTokenPriority(pool.quote)
                    );
                }
                // sort by relative popularity of tokens in the pool
                return getPoolPriority(poolB) - getPoolPriority(poolA);
            },
        );
        // send data to `useState()` hook which returns to the app
        setOutputPools(sortedPools);
    }, [validatedInput]);

    // array of range positions to output from the hook
    const [outputPositions, setOutputPositions] = useState<PositionIF[]>([]);

    // logic to update the output range positions from the hook
    // this does NOT apply token verification because data is specific to user
    // presumably user wants to see their position data regardless of token validity
    useEffect(() => {
        // fn to filter range positions by address (must be exact, will fix for casing mismatch)
        const searchByAddress = (addr: string): PositionIF[] =>
            positionList
                // filter positions for ones with a matching address
                .filter((position: PositionIF) =>
                    matchSearchInput(
                        addr,
                        [position.base, position.quote],
                        SEARCH_EXACT,
                    ),
                )
                // remove empty positions from search results
                .filter((pos: PositionIF) => pos.totalValueUSD);
        // fn to filter range positions by symbol (must be exact IF input is two characters)
        const searchByNameOrSymbol = (symb: string): PositionIF[] =>
            positionList
                .filter((position: PositionIF) =>
                    matchSearchInput(symb, [
                        position.baseSymbol,
                        position.quoteSymbol,
                        position.baseName,
                        position.quoteName,
                    ]),
                )
                // remove empty positions from search results
                .filter((pos: PositionIF) => pos.totalValueUSD);
        // fn to return list of range positions with no search filtering
        const noSearch = (): PositionIF[] => positionList;
        // output variable to hold the array of range positions generated by the search filter
        let filteredRangePositions: PositionIF[];
        // logic router to apply the relevant search function to filter range positions
        switch (searchAs) {
            case 'address':
                filteredRangePositions = searchByAddress(validatedInput);
                break;
            case 'nameOrSymbol':
                filteredRangePositions = searchByNameOrSymbol(validatedInput);
                break;
            case '':
            default:
                filteredRangePositions = noSearch();
        }
        // send positions filtered by search input to local state (to be returned from this file)
        setOutputPositions(filteredRangePositions);
    }, [positionList.length, validatedInput]);

    // array of range positions to output from the hook
    const [outputTxs, setOutputTxs] = useState<TransactionIF[]>([]);

    // logic to update the output txs from the hook
    // this does NOT apply token verification because data is specific to user
    // presumably user wants to see their txs regardless of token validity
    useEffect(() => {
        // fn to filter txs by address (must be exact, will fix for casing mismatch)
        const searchByAddress = (addr: string): TransactionIF[] =>
            txList.filter((tx: TransactionIF) =>
                matchSearchInput(addr, [tx.base, tx.quote], SEARCH_EXACT),
            );
        // fn to filter txs by symbol (must be exact IF input is two characters)
        const searchByNameOrSymbol = (symb: string): TransactionIF[] =>
            txList.filter((tx: TransactionIF) =>
                matchSearchInput(symb, [
                    tx.baseSymbol,
                    tx.quoteSymbol,
                    tx.baseName,
                    tx.quoteName,
                ]),
            );
        // fn to return array of txs with no search filtering
        const noSearch = (): TransactionIF[] => txList;
        // output variable to hold the array of txs generated by the search filter
        let filteredTxs: TransactionIF[];
        // logic router to apply the relevant search function to filter txs
        switch (searchAs) {
            case 'address':
                filteredTxs = searchByAddress(validatedInput);
                break;
            case 'nameOrSymbol':
                filteredTxs = searchByNameOrSymbol(validatedInput);
                break;
            case '':
            default:
                filteredTxs = noSearch();
        }
        // send txs filtered by search input to local state (to be returned from this file)
        setOutputTxs(filteredTxs);
    }, [txList.length, validatedInput]);

    // array of range positions to output from the hook
    const [outputLimits, setOutputLimits] = useState<LimitOrderIF[]>([]);

    // logic to update the output txs from the hook
    // this does NOT apply token verification because data is specific to user
    // presumably user wants to see their txs regardless of token validity
    useEffect(() => {
        // fn to filter txs by address (must be exact, will fix for casing mismatch)
        const searchByAddress = (addr: string): LimitOrderIF[] =>
            limitOrderList.filter((limitOrder: LimitOrderIF) =>
                matchSearchInput(
                    addr,
                    [limitOrder.base, limitOrder.quote],
                    SEARCH_EXACT,
                ),
            );
        // fn to filter txs by symbol (must be exact IF input is two characters)
        const searchByNameOrSymbol = (symb: string): LimitOrderIF[] =>
            limitOrderList.filter((limitOrder: LimitOrderIF) =>
                matchSearchInput(symb, [
                    limitOrder.baseSymbol,
                    limitOrder.quoteSymbol,
                    limitOrder.baseName,
                    limitOrder.quoteName,
                ]),
            );
        // fn to return array of txs with no search filtering
        const noSearch = (): LimitOrderIF[] => limitOrderList;
        // output variable to hold the array of txs generated by the search filter
        let filteredLimits: LimitOrderIF[];
        // logic router to apply the relevant search function to filter txs
        switch (searchAs) {
            case 'address':
                filteredLimits = searchByAddress(validatedInput);
                break;
            case 'nameOrSymbol':
                filteredLimits = searchByNameOrSymbol(validatedInput);
                break;
            case '':
            default:
                filteredLimits = noSearch();
        }
        // send txs filtered by search input to local state (to be returned from this file)
        setOutputLimits(filteredLimits);
    }, [limitOrderList.length, validatedInput]);

    return {
        rawInput,
        setInput: setRawInput,
        clearInput: clearInput,
        isInputValid: !!searchAs,
        pools: outputPools,
        positions: outputPositions,
        txs: outputTxs,
        limits: outputLimits,
    };
};
